import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Event, NavigationStart, Router } from '@angular/router';
import { filter, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BrowserStateService {
  readonly scrollYPosition = new Map<string, number>();
  private readonly router = inject(Router);

  /**
   * A reactive signal that emits a `NavigationStart` event when triggered by a browser's popstate navigation.
   *
   * The `popstateEvent` variable leverages the `toSignal` utility to create a reactive signal based on
   * Angular’s router events. Specifically, it filters events to include only instances of `NavigationStart`
   * where the `navigationTrigger` is `'popstate'`, such as when the user navigates via browser history
   * (e.g., Back or Forward actions). Events that do not meet this condition will result in `null`.
   *
   * This reactive signal can be used to respond to or track browser navigation triggered by popstate events.
   *
   * @type {Signal<NavigationStart | null>}
   *
   * @remarks
   * - The signal's initial value is explicitly set to `null`.
   * - Filtering is performed using RxJS operators (`filter` and `map`) to ensure that only relevant
   *   navigation events are emitted.
   * - This is especially useful in scenarios where you need to perform logic based on navigation origin
   *   (e.g., detecting history changes vs. programmatic navigations).
   *
   * @example
   * ```typescript
   * // Example usage in a component:
   * effect(() => {
   *       const popstate = this.browserStateService.popstateEvent();
   *       if (popstate) {
   *         const scrollYPosition = this.browserStateService.scrollYPosition.get('app-listing-modeles') ?? 0;
   *         LogsService.info(
   *           'ListingModelesComponent',
   *           `Browser back navigation detected – restoring scroll position to ${scrollYPosition}px`
   *         );
   *         this.window.setTimeout(() => {
   *           this.window.scrollTo({
   *             top: scrollYPosition,
   *             behavior: 'smooth',
   *           });
   *         }, 400);
   *       }
   *     });
   * ```
   *
   * @see {@link NavigationStart} for more details on the router event.
   * @see {@link https://rxjs.dev/} for RxJS operators.
   * @see {@link https://angular.io/guide/router} for Angular routing details.
   *
   * @param toSignal - Converts an observable stream into a reactive signal.
   * @param initialValue - The initial value for the signal, set to `null` here.
   */
  readonly popstateEvent = toSignal(
    this.router.events.pipe(
      filter((event: Event): event is NavigationStart => event instanceof NavigationStart),
      map((event: NavigationStart) => (event.navigationTrigger === 'popstate' ? event : null))
    ),
    { initialValue: null }
  );
}
